<template>
  <!-- Dialog only. The button that opens it lives in the page header, so the parent
       renders that from the `count-changed` it receives and calls open() here — a
       rarely-wanted list shouldn't hold page real estate, and putting it below the
       recordings meant it drifted further away with every "See More". -->
  <div
    id="deletedRecordingsModal"
    ref="modalRef"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="deletedRecordingsModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header d-flex justify-content-between align-items-center">
          <div>
            <h3 id="deletedRecordingsModalTitle" class="modal-title m-0"><strong>Recordings bin</strong></h3>
            <small class="text-muted">
              Kept for {{ retentionDays }} days, then deleted for good.
            </small>
          </div>
          <button type="button" class="close btn-link" data-bs-dismiss="modal" aria-label="Close">
            <span>&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-4">
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </div>

          <p v-else-if="recordings.length === 0" class="text-muted text-center py-4 mb-0">
            The bin is empty. Recordings you delete are kept here for {{ retentionDays }} days.
          </p>

          <div
            v-for="recording in recordings"
            v-else
            :key="recording.id"
            class="deleted-recordings__row d-flex align-items-center gap-3 py-3"
            data-testid="deleted-recording-row"
          >
            <img
              v-if="recording.thumbnailUrl"
              :src="recording.thumbnailUrl"
              alt=""
              class="deleted-recordings__thumb rounded"
            />
            <div v-else class="deleted-recordings__thumb deleted-recordings__thumb--empty rounded"></div>

            <div class="flex-grow-1 deleted-recordings__details">
              <div class="text-truncate"><strong>{{ recording.title || 'Untitled recording' }}</strong></div>
              <div class="text-muted"><small>{{ statsFor(recording) }}</small></div>
            </div>

            <span
              v-if="retentionLabelFor(recording)"
              class="badge rounded-pill bg-light text-dark flex-shrink-0"
              data-testid="deleted-recording-retention"
            >
              {{ retentionLabelFor(recording) }}
            </span>

            <button
              class="btn btn-sm btn-secondary flex-shrink-0 d-flex align-items-center"
              type="button"
              :disabled="restoringIds.includes(recording.id)"
              data-testid="deleted-recording-restore"
              @click="restore(recording)"
            >
              <span
                v-if="restoringIds.includes(recording.id)"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <i v-else class="material-icons me-1" style="font-size:17px">restore</i>
              {{ restoringIds.includes(recording.id) ? 'Restoring...' : 'Restore' }}
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-warning mt-3 mb-0" role="alert">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Modal } from 'bootstrap'
import graph_client from '../../../utils/graphql/graph_client'
import {
  RestoreRecording,
  SearchDeletedUserRecordings,
} from '../../../utils/graphql/recorded-trail-graph'
import { RETENTION_DAYS, retentionLabel } from '../../../utils/helpers/deletedRecordingRetention'
import { recordingStatsSummary } from '../../../utils/helpers/recordingStatsSummary'

export default {
  name: 'DeletedRecordings',
  emits: ['restored', 'count-changed'],
  data () {
    return {
      recordings: [] as any[],
      restoringIds: [] as string[],
      loading: false,
      errorMessage: '' as string,
      retentionDays: RETENTION_DAYS,
      modalInstance: null as Modal | null,
    }
  },
  mounted () {
    if (this.$refs.modalRef) {
      this.modalInstance = Modal.getOrCreateInstance(this.$refs.modalRef as HTMLElement)
    }
    // Loaded up front, not on open, because the header button only appears once we know
    // there is something to show.
    this.load()
  },
  beforeUnmount () {
    this.modalInstance?.hide()
  },
  methods: {
    open () {
      // Refresh on open so a stale count from page load can't show an empty dialog.
      this.load()
      this.modalInstance?.show()
    },

    async load () {
      this.loading = this.recordings.length === 0
      try {
        const result = await graph_client.request(SearchDeletedUserRecordings, { page: 1, perPage: 100 })
        this.recordings = result?.searchUserRecordings ?? []
      } catch (err) {
        // A failure here must not take the recordings list down with it — the button
        // simply doesn't appear.
        console.warn('Failed to load deleted recordings:', err)
        this.recordings = []
      } finally {
        this.loading = false
        this.$emit('count-changed', this.recordings.length)
      }
    },

    // `now` is read per render rather than cached at load, so a page left open
    // overnight doesn't keep showing yesterday's countdown.
    retentionLabelFor (recording: any): string | null {
      return retentionLabel(recording?.deletedAt, new Date())
    },

    statsFor (recording: any): string {
      return recordingStatsSummary(recording)
    },

    async restore (recording: any) {
      if (this.restoringIds.includes(recording.id)) return

      this.errorMessage = ''
      this.restoringIds = [...this.restoringIds, recording.id]

      try {
        const result = await graph_client.request(RestoreRecording, { id: recording.id })
        const payload = result?.restoreRecording

        if (payload?.restored) {
          this.recordings = this.recordings.filter((r: any) => r.id !== recording.id)
          this.$emit('count-changed', this.recordings.length)
          // The parent owns the live list; it refetches rather than us guessing where
          // the restored recording belongs in the current sort and filters.
          this.$emit('restored', recording.id)
          if (this.recordings.length === 0) this.modalInstance?.hide()
        } else {
          this.errorMessage = payload?.errors?.[0]?.errors?.[0]
            || 'That recording could not be restored. It may have already been removed permanently.'
        }
      } catch (err) {
        console.error('Restore recording failed:', err)
        this.errorMessage = 'Something went wrong restoring that recording. Please try again.'
      } finally {
        this.restoringIds = this.restoringIds.filter((id: string) => id !== recording.id)
      }
    },
  },
}
</script>

<style scoped>
.deleted-recordings__row + .deleted-recordings__row {
  border-top: 1px solid var(--bs-border-color, #dee2e6);
}

.deleted-recordings__thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  flex-shrink: 0;
}

.deleted-recordings__thumb--empty {
  background-color: var(--bs-secondary-bg, #e9ecef);
}

/* Lets the title truncate instead of pushing the pill and button off the row */
.deleted-recordings__details {
  min-width: 0;
}
</style>
